import { useTranslation } from 'next-i18next';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { ToastType, useToasts } from '@hub/hooks/useToasts';
import { ProgressIndicator, ProgressShape, ProgressThickness } from '@icommerce/ui';

export enum SpinnerSize {
  S = 'small',
  M = 'medium',
}

export enum QueryStatusName {
  success = 'success',
  pending = 'pending',
  error = 'error',
  idle = 'idle',
}

type QueryStatusHandlerProps = {
  status: QueryStatusName;
  style?: Record<string, string>;
  loaderText?: string;
  children: ReactElement;
  spinnerSize?: SpinnerSize;
};

export const IbhQueryStatusHandler = ({
  status,
  children,
  style,
  loaderText,
  spinnerSize = SpinnerSize.M,
}: QueryStatusHandlerProps) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const spinnerDimension: Record<SpinnerSize, number> = {
    small: 15,
    medium: 30,
  };

  useEffect(() => {
    if (status === QueryStatusName.error) {
      addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
    }
  }, [status, addToast, t]);

  switch (status) {
    case QueryStatusName.pending:
      return (
        <div data-testid="spinner" className="flex justify-center w-full items-center absolute" style={style}>
          <ProgressIndicator
            label={loaderText || t('COMMON.LOADING', { ns: 'free-form-hub/freeFormHubCommon' })}
            shape={ProgressShape.Circular}
            thickness={ProgressThickness.Thin}
            size={spinnerDimension[spinnerSize]}
          />
          <p className="ml-8 text-sm">{loaderText}</p>
        </div>
      );
    case QueryStatusName.success:
    case QueryStatusName.idle:
      return children;
    // "error", and the rest
    default:
      return (
        <>
          <Icon id={IconID.WARNING} size={7} color={IconColor.GRAY} />
          &nbsp;{t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' })}
        </>
      );
  }
};
